import { Link } from "react-router-dom";
import { Clock, Heart, Sparkles, ArrowRight } from "lucide-react";
import { useEffect, useRef } from "react";

const CALENDLY_URL = "https://calendly.com/khalilah-theblackupstart/30min";
const CALENDLY_PARENT_URL = `${CALENDLY_URL}?hide_event_type_details=0&hide_gdpr_banner=1&primary_color=c6a87c`;

export default function BookNow() {
  const widgetRef = useRef(null);

  // Calendly script is preloaded in index.html. Initialize widget as soon as
  // the script is ready — usually instantly on second visit, < 1s on first.
  useEffect(() => {
    let cancelled = false;

    const init = () => {
      if (cancelled || !widgetRef.current) return;
      if (window.Calendly && typeof window.Calendly.initInlineWidget === "function") {
        // Avoid double-init if already rendered (e.g., StrictMode)
        if (widgetRef.current.querySelector("iframe")) return;
        window.Calendly.initInlineWidget({
          url: CALENDLY_PARENT_URL,
          parentElement: widgetRef.current,
        });
        return true;
      }
      return false;
    };

    if (init()) return;

    // Script may still be loading — poll briefly, then fall back to load event
    const interval = setInterval(() => {
      if (init()) clearInterval(interval);
    }, 50);

    const onLoad = () => init();
    const script = document.querySelector(
      'script[src^="https://assets.calendly.com/assets/external/widget.js"]'
    );
    script?.addEventListener("load", onLoad);

    return () => {
      cancelled = true;
      clearInterval(interval);
      script?.removeEventListener("load", onLoad);
    };
  }, []);

  return (
    <div data-testid="page-book">
      {/* HEADER */}
      <section className="px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-14 text-center">
        <p className="overline text-[var(--gold)] rise">Book your session</p>
        <h1 className="font-serif-display mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[var(--indigo)] rise rise-d1">
          Pick a time.{" "}
          <span className="italic text-[var(--gold)]">Bring your real questions.</span>
        </h1>
        <p className="mt-7 max-w-2xl mx-auto text-[1.05rem] leading-relaxed text-[var(--ink-muted)] rise rise-d2">
          Thirty focused minutes to get your content strategy aligned with the
          legacy you're actually building. No pressure, no pitch — just clarity.
        </p>
      </section>

      {/* WHAT TO EXPECT */}
      <section
        className="px-6 md:px-12 lg:px-16 pb-12"
        data-testid="book-what-to-expect"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {[
            {
              icon: Clock,
              k: "Thirty minutes",
              v: "One focused, no-fluff conversation built around your business — not a sales script.",
            },
            {
              icon: Heart,
              k: "Warm, not weird",
              v: "Show up as you are. Hair scarf, baby on hip, coffee in hand — we're not performing here.",
            },
            {
              icon: Sparkles,
              k: "You leave with clarity",
              v: "A sharper message, a clear next step, and language that finally sounds like you.",
            },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <div
                key={b.k}
                className="border-l border-[var(--gold)]/50 pl-6 py-2"
                data-testid={`book-expect-${b.k.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <Icon size={20} className="text-[var(--gold)] mb-4" />
                <p className="font-serif-display text-2xl text-[var(--indigo)] leading-snug">
                  {b.k}.
                </p>
                <p className="mt-3 text-[0.98rem] text-[var(--ink-muted)] leading-relaxed">
                  {b.v}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CALENDLY EMBED */}
      <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32" data-testid="book-calendly-section">
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            <div
              className="absolute -top-4 -left-4 w-full h-full border border-[var(--gold)]/50 pointer-events-none"
              aria-hidden
            />
            <div
              ref={widgetRef}
              className="calendly-inline-widget calendly-wrap relative"
              data-url={CALENDLY_PARENT_URL}
              style={{ minWidth: "320px", height: "760px" }}
              data-testid="calendly-embed"
            />
            <noscript>
              <p className="p-8 text-center">
                Please enable JavaScript to view the booking widget, or{" "}
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[var(--gold)]"
                >
                  open Calendly directly
                </a>
                .
              </p>
            </noscript>
          </div>

          <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-sm text-[var(--ink-muted)]">
            <p>
              Trouble seeing the calendar?{" "}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--indigo)] underline underline-offset-4 hover:text-[var(--gold)]"
                data-testid="book-calendly-fallback-link"
              >
                Open the full Calendly page →
              </a>
            </p>
            <p className="overline text-[var(--ink-muted)]">
              Powered by Calendly · Times shown in your timezone
            </p>
          </div>
        </div>
      </section>

      {/* CLOSING REASSURANCE */}
      <section
        className="bg-[var(--indigo)] text-[var(--cream)] px-6 md:px-12 lg:px-16 py-24 md:py-28 text-center grain relative"
        data-testid="book-closing"
      >
        <div className="max-w-3xl mx-auto">
          <p className="overline text-[var(--gold)]">A note from Khalilah</p>
          <p className="font-serif-display text-2xl md:text-3xl lg:text-[2.2rem] leading-[1.3] mt-6 italic">
            "You don't have to come in with it all figured out. That's literally
            my job. Just bring the version of you that's tired of overthinking
            it — we'll do the rest together."
          </p>
          <Link to="/services" className="btn-ghost-dark mt-10 inline-flex" data-testid="book-cta-services">
            See Services Again
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
